import {
  RefObject,
  useEffect,
  useState,
} from "react";

import { isIos } from "../../utils";

type FullscreenTarget = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void,
  msRequestFullscreen?: () => Promise<void> | void,
  mozRequestFullScreen?: () => Promise<void> | void,
};

type FullscreenDoc = Document & {
  webkitExitFullscreen?: () => Promise<void> | void,
  msExitFullscreen?: () => Promise<void> | void,
  mozCancelFullScreen?: () => Promise<void> | void,

  webkitFullscreenElement?: Element | null,
  msFullscreenElement?: Element | null,
  mozFullScreenElement?: Element | null,

  fullscreenEnabled?: boolean,
  webkitFullscreenEnabled?: boolean,
  msFullscreenEnabled?: boolean,
  mozFullScreenEnabled?: boolean,
};

type Options = {
  forceCssFallback?: boolean,
  preferCssFallback?: boolean,
  onChange?: (isFullscreen: boolean) => void,
};

const isTelegramEnv = () => {
  if (typeof window === "undefined") return false;
  const ua = (navigator.userAgent || "").toLowerCase();
  return ua.includes("telegram") || !!(window as any).Telegram?.WebApp;
};

const isFullscreenApiSupported = (el: FullscreenTarget | null) => {
  if (typeof document === "undefined" || !el) return false;
  const d = document as FullscreenDoc;

  const hasReq =
    !!el.requestFullscreen ||
    !!el.webkitRequestFullscreen ||
    !!el.msRequestFullscreen ||
    !!el.mozRequestFullScreen;

  const enabled =
    document.fullscreenEnabled ||
    !!d.webkitFullscreenEnabled ||
    !!d.msFullscreenEnabled ||
    !!d.mozFullScreenEnabled;

  return hasReq && enabled;
};

export function useFullscreen(targetRef: RefObject<HTMLElement | null>,
  opts: Options = {}) {
  const {
    forceCssFallback,
    preferCssFallback,
    onChange,
  } = opts;

  const [ isFullscreen, setIsFullscreen ] = useState(false);
  const [ isCssFallback, setIsCssFallback ] = useState(false);

  const getTarget = (): FullscreenTarget | null =>
    (targetRef.current as FullscreenTarget | null) ?? null;

  const getFullscreenElement = (): Element | null => {
    const d = document as FullscreenDoc;
    return (
      document.fullscreenElement ??
      d.webkitFullscreenElement ??
      d.msFullscreenElement ??
      d.mozFullScreenElement ??
      null
    );
  };

  const shouldUseCssFallback = (() => {
    if (forceCssFallback) return true;
    const el = getTarget();
    const apiSupported = isFullscreenApiSupported(el);
    const hostileEnv = isIos() || isTelegramEnv();
    if (preferCssFallback && hostileEnv) return true;
    if (!apiSupported) return true;
    return hostileEnv;
  })();

  const request = async () => {
    const t = getTarget();
    if (!t) throw new Error("No target for fullscreen");

    if (t.requestFullscreen) return await t.requestFullscreen();
    if (t.webkitRequestFullscreen) return await t.webkitRequestFullscreen();
    if (t.msRequestFullscreen) return await t.msRequestFullscreen();
    if (t.mozRequestFullScreen) return await t.mozRequestFullScreen();

    throw new Error("Fullscreen API not supported");
  };

  const exitNative = async () => {
    const d = document as FullscreenDoc;
    if (document.exitFullscreen) return await document.exitFullscreen();
    if (d.webkitExitFullscreen) return await d.webkitExitFullscreen();
    if (d.msExitFullscreen) return await d.msExitFullscreen();
    if (d.mozCancelFullScreen) return await d.mozCancelFullScreen();
  };

  const enter = async () => {
    const el = getTarget();

    if (shouldUseCssFallback || !isFullscreenApiSupported(el)) {
      setIsCssFallback(true);
      setIsFullscreen(true);
      onChange?.(true);
      return;
    }

    try {
      await request();
    } catch (e) {
      console.error("Failed to enter fullscreen", e);
      setIsCssFallback(true);
      setIsFullscreen(true);
      onChange?.(true);
    }
  };

  const leave = async () => {
    if (isCssFallback) {
      setIsCssFallback(false);
      setIsFullscreen(false);
      onChange?.(false);
      return;
    }
    try {
      await exitNative();
    } catch(e) {
      console.error("Failed to exit fullscreen", e);
    }
  };

  const toggle = async () => {
    if (isCssFallback) {
      return isFullscreen ? leave() : enter();
    }
    const active = getFullscreenElement();
    const t = getTarget();
    if (active && t && active === t) return await leave();
    return await enter();
  };

  useEffect(() => {
    const handler = () => {
      if (isCssFallback) return;
      const t = getTarget();
      const active = getFullscreenElement();
      const next = !!(t && active === t);
      setIsFullscreen(next);
      onChange?.(next);
    };

    const evs: Array<keyof DocumentEventMap | string> = [
      "fullscreenchange",
      "webkitfullscreenchange",
      "msfullscreenchange",
      "mozfullscreenchange",
    ];
    evs.forEach((ev) => document.addEventListener(ev as any, handler));
    handler();

    return () => {
      evs.forEach((ev) => document.removeEventListener(ev as any, handler));
    };
  }, [ isCssFallback, onChange ]);

  useEffect(() => {
    if (!isCssFallback) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") void leave();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [ isCssFallback ]);

  useEffect(() => {
    if (!isCssFallback || !isFullscreen) return;

    const isIOS = isIos();
    const root = document.documentElement;
    const body = document.body;

    const prevOverflow = root.style.overflow;
    const prevPaddingRight = root.style.paddingRight;
    const prevBodyPos = body.style.position;
    const prevBodyTop = body.style.top;
    const prevBodyLeft = body.style.left;
    const prevBodyRight = body.style.right;
    const prevBodyWidth = body.style.width;

    const scrollY =
      window.scrollY ||
      root.scrollTop ||
      body.scrollTop ||
      0;

    const scrollbarWidth = window.innerWidth - root.clientWidth;

    if (isIOS) {
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
    } else {
      root.style.overflow = "hidden";
      if (scrollbarWidth > 0) {
        root.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    const preventTouch = (e: TouchEvent) => e.preventDefault();
    document.addEventListener(
      "touchmove", preventTouch, {
        passive: false,
      },
    );

    return () => {
      document.removeEventListener("touchmove", preventTouch);

      if (isIOS) {
        body.style.position = prevBodyPos;
        body.style.top = prevBodyTop;
        body.style.left = prevBodyLeft;
        body.style.right = prevBodyRight;
        body.style.width = prevBodyWidth;
        window.scrollTo(0, scrollY);
      } else {
        root.style.overflow = prevOverflow;
        root.style.paddingRight = prevPaddingRight;
      }
    };
  }, [ isCssFallback, isFullscreen ]);


  return {
    isFullscreen,
    isCssFallback,
    isNativeSupported: isFullscreenApiSupported(getTarget()),
    enter,
    exit: leave,
    toggle,
  };
}
